'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthService, User, LoginCredentials } from '@/lib/auth';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    // Verify existing token on mount
    const verifyAuth = async () => {
      try {
        const verifiedUser = await AuthService.verifyToken();
        setUser(verifiedUser);
      } catch (error) {
        console.error('Auth verification failed:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();

    // Set up automatic token refresh every 45 minutes (before 1 hour expiration)
    const refreshInterval = setInterval(async () => {
      if (AuthService.isAuthenticated()) {
        try {
          const refreshResult = await AuthService.refreshToken();
          if (refreshResult) {
            setUser(refreshResult.user);
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error('Auto token refresh failed:', error);
          setUser(null);
        }
      }
    }, 45 * 60 * 1000); // 45 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const { user: authenticatedUser } = await AuthService.login(credentials);
      setUser(authenticatedUser);
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await AuthService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear local state even if server logout fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const refreshResult = await AuthService.refreshToken();
      if (refreshResult) {
        setUser(refreshResult.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}